import { Area } from "../area/Area";
import { BoundingRect, getBoundingRectFromMinAndSize } from "../BoundingRect";
import { ModelConfig } from "../modelConfig/ModelConfig";
import { MeshStats } from "../modelConfig/MeshStats";
import { RawModel } from "../rawModel/RawModel";
import { MeshInstance, createMeshInstances } from "./MeshInstance";
import { ElementInstance, Element, createElementInstance } from "../element/Element";
import { LayoutFunctions } from "../area/LayoutFunctions";
import {
    getDefaultMaterialSlotMapping,
    MaterialSlotMapping,
} from "../material/getDefaultMaterialSlotMapping";
import { MaterialResources } from "@/state/MaterialResources";
import { createGroupInstances, GroupInstance, GroupInstanceCreationOptions } from "./GroupInstance";
import _ from "lodash";

type ModelCreationOptions = {
    skipUvResize?: boolean;
    groupInstanceCreationOptions?: GroupInstanceCreationOptions[];
};

export type ModelInstance = {
    raw: RawModel;
    config: ModelConfig;
    meshNames: string[];
    meshes: Record<string, MeshInstance>;
    meshAreas: Record<string, Area>;
    elementInstances: ElementInstance[];
    size: { x: number; y: number; z: number };
    box: BoundingRect;
    position: { x: number; y: number };
    rotation: number;
    materialSlotMapping: MaterialSlotMapping;
    groups: GroupInstance[];
};

export const createModelInstance = (
    model: RawModel,
    config: ModelConfig,
    meshStats: Record<string, MeshStats>,
    materials: Record<string, MaterialResources>,
    targetRectProp?: BoundingRect,
    options: ModelCreationOptions = {}
): ModelInstance => {
    const meshNames = model.meshes.map((mesh) => mesh.name);
    const targetSize = {
        x: model.boundingRect.sizeX,
        y: model.boundingRect.sizeY,
        z: model.boundingRect.sizeZ,
    };
    const targetRect =
        targetRectProp ?? getBoundingRectFromMinAndSize({ x: 0, y: 0, z: 0 }, targetSize);

    const meshInstances = createNewMeshInstances(
        model,
        config,
        (meshName) => meshStats[meshName],
        targetRect,
        options
    );

    const groupInstances = createGroupInstances(
        config,
        (meshName) => meshStats[meshName],
        model,
        targetRect,
        options.skipUvResize,
        options.groupInstanceCreationOptions
    );

    const meshAreaKeys = Object.keys(config.meshAreas);
    const meshAreas = meshAreaKeys.reduce((acc, meshAreaKey) => {
        const meshArea = config.meshAreas[meshAreaKey];
        acc[meshAreaKey] = new Area(meshArea.layout, meshInstances[meshAreaKey].box);
        return acc;
    }, {} as Record<string, Area>);

    return {
        raw: model,
        config,
        meshNames,
        meshes: meshInstances,
        meshAreas,
        elementInstances: [],
        size: targetSize,
        box: targetRect,
        materialSlotMapping: getDefaultMaterialSlotMapping(config.materialSlots, materials),
        groups: groupInstances,
        position: { x: 0, y: 0 },
        rotation: 0,
    };
};

export const resizeModelInstance = (
    modelInstance: ModelInstance,
    meshStats: Record<string, MeshStats>,
    targetDimensions: { x: number; y: number; z: number },
    options: ModelCreationOptions = {}
): ModelInstance => {
    const targetBoundaries = getBoundingRectFromMinAndSize({ x: 0, y: 0, z: 0 }, targetDimensions);
    const meshAreas = modelInstance.meshAreas;

    const meshStatsProvider = (meshName: string) => meshStats[meshName];

    const meshInstances = createNewMeshInstances(
        modelInstance.raw,
        modelInstance.config,
        meshStatsProvider,
        targetBoundaries,
        options
    );

    const groupInstances = createGroupInstances(
        modelInstance.config,
        meshStatsProvider,
        modelInstance.raw,
        targetBoundaries,
        options.skipUvResize
    );

    const instances = modelInstance.elementInstances;
    for (const meshName of Object.keys(meshAreas)) {
        const area = meshAreas[meshName];

        const oldSpace = area.getSpace();
        area.applyBoundaries(meshInstances[meshName].box);

        const newSpace = area.getSpace();

        for (let i = 0; i < instances.length; i++) {
            const instance = instances[i];
            const element = modelInstance.config.elements[instance.elementId];
            if (element.parentAreaName !== meshName) {
                continue;
            }

            const resizedSpace = LayoutFunctions["1axis"].resizeSpace(
                instance.area.getSpace(),
                oldSpace,
                newSpace
            );

            const childBox = area.getChildBox(resizedSpace);

            if (!childBox) {
                continue;
            }

            instances[i] = createElementInstance(
                element,
                childBox,
                modelInstance.meshes[element.meshName].raw,
                modelInstance.meshes[element.meshName].stats
            );
        }
    }

    return {
        ...modelInstance,
        meshes: meshInstances,
        groups: groupInstances,
        elementInstances: instances,
        size: { ...targetDimensions },
        box: targetBoundaries,
    };
};

export const addElementInstance = (
    modelInstance: ModelInstance,
    element: Element
): ModelInstance => {
    const area = modelInstance.meshAreas[element.parentAreaName];
    const instances = modelInstance.elementInstances;
    if (!area) {
        console.error(`Area ${element.parentAreaName} not found in model`);
        return modelInstance;
    }

    const space = LayoutFunctions["1axis"].getFreeSpaceForConfig(
        area.getSpace(),
        instances.map((i) => i.area.getSpaceForLayout(area.layout)),
        element.areaConfig
    );

    if (!space) {
        console.error(`No free space for element ${element.id}`);
        return modelInstance;
    }

    const restrictedSpace = LayoutFunctions["1axis"].applyConfig(space, element.areaConfig).space;

    const childBox = area.getChildBox(restrictedSpace);
    if (!childBox) {
        console.error(`No child box for element ${element.id}`);
        return modelInstance;
    }

    const newInstance = createElementInstance(
        element,
        childBox,
        modelInstance.meshes[element.meshName].raw,
        modelInstance.meshes[element.meshName].stats
    );

    return { ...modelInstance, elementInstances: [...instances, newInstance] };
};

const createNewMeshInstances = (
    model: RawModel,
    config: ModelConfig,
    meshStatsProvider: (meshName: string) => MeshStats,
    targetBoundaries: BoundingRect,
    options: ModelCreationOptions
) => {
    const groupMeshNames = _.uniq(
        Object.values(config.meshGroups).flatMap((group) => group.meshNames)
    );

    const meshInstances = createMeshInstances(
        model.meshes.filter(({ name }) => !groupMeshNames.includes(name)),
        config,
        meshStatsProvider,
        model.boundingRect,
        targetBoundaries,
        options.skipUvResize
    );

    return meshInstances;
};
